"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FileText } from "lucide-react";



export const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About Us" },
  { href: "/services", label: "Services" },
  { href: "/projects", label: "Projects" },
  { href: "/contact", label: "Contact" },

  // 🔽 NEW DROPDOWN
{
  label: "Renvixx CP",
  icon: FileText, // 👈 ADD ICON HERE
  dropdown: [
    {
      label: "Preview PDF",
      href: "/pdfs/renvixx.pdf",
      type: "preview",
    },
    {
      label: "Download PDF",
      href: "/pdfs/renvixx.pdf",
      type: "download",
    },
  ],
}

];

export default function Header({ initialSettings }: { initialSettings?: any }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(initialSettings?.auth_logo || null);
  const [openDropdown, setOpenDropdown] = useState(false);

  const [isSvg, setIsSvg] = useState(initialSettings?.auth_logo?.toLowerCase().endsWith(".svg") || false);
  const pathname = usePathname();

  useEffect(() => {
    if (logoUrl) return; // Skip if provided by props

    // Try cached logo first
    const cachedLogo = localStorage.getItem("siteLogo");
    const cachedIsSvg = localStorage.getItem("isSvg") === "true";

    if (cachedLogo) {
      setLogoUrl(cachedLogo);
      setIsSvg(cachedIsSvg);
    }

    // Always try fetching latest from API (priority)
    const fetchLogo = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/settings", {
          headers: { Accept: "application/json" },
          cache: "no-store", // always get the latest logo
        });

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }

        const data = await res.json();
        const logo = data?.data?.auth_logo;

        if (logo) {
          const svg = logo.toLowerCase().endsWith(".svg");
          setLogoUrl(logo);
          setIsSvg(svg);

          // Cache it for next load
          localStorage.setItem("siteLogo", logo);
          localStorage.setItem("isSvg", String(svg));
        } else if (!cachedLogo) {
          setLogoUrl("/Renvixx-Logo1.png"); // fallback
          setIsSvg(false);
        }
      } catch (err) {
        console.error("⚠️ Failed to fetch logo:", err);
        if (!cachedLogo) {
          setLogoUrl("/Renvixx-Logo1.png");
          setIsSvg(false);
        }
      }
    };

    fetchLogo();
  }, []);

  return (
    <header className="w-full font-outfit bg-white shadow-sm border-b border-gray-200 z-50 sticky top-0">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between py-5">
        {/* ✅ Logo */}
        <div className="flex items-center">
          <Link href="/" aria-label="Renvixx Home">
            <Image
              src="/Renvixx-Logo1.png"
              alt="Renvixx Logo"
              width={250}
              height={70}
              className="object-contain"
              priority
            />
          </Link>
        </div>


{/* Desktop Navigation */}
<nav className="hidden lg:flex space-x-8 font-outfit font-medium relative">
  {navLinks.map((nav) =>
    nav.dropdown ? (
      <div key={nav.label} className="relative group">
        
        {/* Button with Arrow */}
        <button
          className="transition-colors duration-300 text-[#2a6380] group-hover:text-[#33c1c7] flex items-center gap-2"
        >
          {nav.label}

          <ChevronDown
            size={16}
            className="transition-transform duration-300 group-hover:rotate-180 text-[#2a6380] group-hover:text-[#33c1c7]"
          />
        </button>

        {/* Dropdown */}
        <div
          className="
            absolute -left-[75px] top-full
            mt-0
            hidden
            group-hover:block
            bg-white shadow-lg rounded-md py-2 w-40 z-50
          "
        >
          {nav.dropdown.map((item, index) => (
            <Link
              key={index}
              href={item.href}
              target={item.type === 'preview' ? '_blank' : undefined}
              download={item.type === 'download' ? true : undefined}
              className="block px-4 py-2 text-sm text-[#2a6380] hover:bg-gray-100 hover:text-[#33c1c7]"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    ) : (
      <Link
        key={nav.href}
        href={nav.href}
        className={`transition-colors duration-300 ${
          pathname === nav.href
            ? 'text-[#33c1c7]'
            : 'text-[#2a6380] hover:text-[#33c1c7]'
        }`}
      >
        {nav.label}
      </Link>
    )
  )}
</nav>




        {/* ✅ Desktop Login Button */}
        {/* <Link
          href="https://portal.renvixx.com/"
          className="relative overflow-hidden hidden lg:inline-block border-2 border-[#33c1c7] bg-[#33c1c7] text-[#2a6380] font-semibold px-4 py-3 rounded transition-all duration-500 ease-in-out group"
        >
          <span className="relative z-10 transition-colors duration-500 group-hover:text-[#33c1c7]">
            Customer Login
          </span>
          <span className="absolute inset-0 bg-white -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
        </Link> */}

        {/* ✅ Mobile Menu Button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden text-gray-700 hover:text-[#33c1c7] focus:outline-none"
        >
          {menuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

{/* ✅ Mobile Menu */}
{menuOpen && (
  <div className="lg:hidden bg-white shadow-md border-t border-gray-200 animate-slideDown">
    <nav className="flex flex-col space-y-4 px-6 py-6 text-base font-medium text-center">

      {navLinks.map((nav) =>
        nav.dropdown ? (
          <div key={nav.label} className="w-full">
            {/* Dropdown Button */}
            <button
              onClick={() => setOpenDropdown(!openDropdown)}
              className="w-full text-[#2a6380] hover:text-[#33c1c7] transition-colors flex justify-center items-center"
            >
              {nav.label}
              <span className="ml-2">
  {openDropdown ? (
    <ChevronUp size={18} className="text-[#2a6380]" />
  ) : (
    <ChevronDown size={18} className="text-[#2a6380]" />
  )}
</span>

            </button>

            {/* Dropdown Items */}
            {openDropdown && (
              <div className="mt-2  rounded-md py-2">
                {nav.dropdown.map((item, index) => (
                  <Link
                    key={index}
                    href={item.href}
                    target={item.type === "preview" ? "_blank" : undefined}
                    download={item.type === "download" ? true : undefined}
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-[#2a6380] hover:text-[#33c1c7] hover:bg-gray-100"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Link
            key={nav.href}
            href={nav.href}
            onClick={() => setMenuOpen(false)}
            className={`w-full transition-colors duration-300 ${
              pathname === nav.href
                ? "text-[#33c1c7]"
                : "text-[#2a6380] hover:text-[#33c1c7]"
            }`}
          >
            {nav.label}
          </Link>
        )
      )}

      {/* Login Button */}
      {/* <Link
        href="https://portal.renvixx.com/"
        onClick={() => setMenuOpen(false)}
        className="relative w-full overflow-hidden bg-[#33c1c7] border-2 border-[#33c1c7] text-[#2a6380] text-center font-semibold text-sm px-4 py-3 rounded transition-all duration-500 ease-in-out group"
      >
        <span className="relative z-10 transition-colors duration-500 group-hover:text-[#33c1c7]">
          Customer Login
        </span>
        <span className="absolute inset-0 bg-white -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
      </Link> */}
    </nav>
  </div>
)}

    </header>
  );
}
